import {
  DownloadSimpleIcon,
  HandCoinsIcon,
  QrCodeIcon,
  UploadSimpleIcon,
} from '@phosphor-icons/react'
import { TFunction } from 'i18next'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Outlet } from 'react-router-dom'

import { useUploadParkingCsv } from '@/api/parking-management'
import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import { NavbarItem } from '@/components/layout/page-header/page-header.types'
import { Button } from '@/components/ui/button'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { Icon } from '@/components/ui/icon'
import { popupFactory, routerFactory, toastFactory } from '@/factories'
import { useFilePicker, useSearchParams } from '@/hooks'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { downloadHref, getApiErrorMessage } from '@/utils'

import {
  MAX_PARKING_CSV_UPLOAD_FILE_SIZE,
  PARKING_MANAGEMENT_TEMPLATE_PATH,
} from './parking-management.page.constants'
import { getParkingCsvUploadMessage } from './parking-management.page.utils'
import * as S from './parking-management.styled'
import { ParkingManagementOutletContext } from './parking-management.types'
import {
  initialResidentsParkingFilters,
  residentsParkingQueryFiltersSchema,
} from './residents-parking/residents-parking.constants'
import { ParkingQrCodeModal } from './visitors-parking/parking-qr-code-modal'
import { ServiceFeeModal } from './visitors-parking/service-fee-modal'

const getResidentsNavbarItem = (t: TFunction<['parking-management']>): NavbarItem => ({
  label: t('parking-management:page.navigation.residents'),
  to: {
    path: '/dashboard/building-dashboard/parking-management/residents',
  },
})

const getVisitorsNavbarItem = (t: TFunction<['parking-management']>): NavbarItem => ({
  label: t('parking-management:page.navigation.visitors'),
  to: { path: '/dashboard/building-dashboard/parking-management/visitors' },
})

export const ParkingManagementPage = () => {
  const { t } = useTranslation(['parking-management'])
  const { Navigate, useMatch } = routerFactory.useRouter()
  const isVisitorsPath = useMatch({
    path: '/dashboard/building-dashboard/parking-management/visitors',
  })
  const isResidentsPath = useMatch({
    path: '/dashboard/building-dashboard/parking-management/residents',
  })

  const activeBuilding = useSelector(selectActiveBuilding)
  const buildingId = activeBuilding?.id
  const parkingManagementAvailable = activeBuilding?.parkingManagementAvailable
  const visitorParkingAvailable = activeBuilding?.visitorParkingAvailable

  const { mutateAsync: uploadParkingCsv, isPending: isUploadParkingCsvPending } =
    useUploadParkingCsv()
  const { clearParams: clearResidentsParkingFilters } = useSearchParams({
    initialState: initialResidentsParkingFilters,
    schema: residentsParkingQueryFiltersSchema,
  })

  const { inputProps, open: openParkingCSVFilePicker } = useFilePicker({
    acceptedTypes: ['.csv'],
    maxFileSize: MAX_PARKING_CSV_UPLOAD_FILE_SIZE,
    maxFiles: 1,
    onFilesSelected: (files) => {
      const file = files[0]
      if (!file || !buildingId) return

      popupFactory.open(ConfirmationPopup, {
        cancelText: t('parking-management:page.uploadCsvPopup.cancel'),
        children: (
          <Trans
            components={[<strong />]}
            i18nKey="page.uploadCsvPopup.body"
            ns="parking-management"
            values={{ fileName: file.name }}
          />
        ),
        confirmText: (
          <>
            <Icon icon={UploadSimpleIcon} color="orange500" />
            {t('parking-management:page.uploadCsvPopup.confirm')}
          </>
        ),
        onConfirm: async () => {
          try {
            const response = await uploadParkingCsv({ buildingId, file })

            toastFactory.success(getParkingCsvUploadMessage(t, response))
            clearResidentsParkingFilters()
          } catch (error) {
            toastFactory.error(getApiErrorMessage(error))
          }
        },
        title: t('parking-management:page.uploadCsvPopup.title'),
        variant: 'warning',
      })
    },
    onValidationError: (errors) =>
      toastFactory.error(
        errors[0]?.errors[0] ?? t('parking-management:page.uploadCsvPopup.invalidFile'),
      ),
  })

  const handleUploadCsvClick = () => {
    openParkingCSVFilePicker()
  }

  const handleParkingQRCodeClick = () => {
    popupFactory.open(ParkingQrCodeModal, {})
  }

  const handleSetServiceFeesClick = () => {
    popupFactory.open(ServiceFeeModal, {})
  }

  if (!parkingManagementAvailable && visitorParkingAvailable && !isVisitorsPath) {
    return <Navigate to={{ path: '/dashboard/building-dashboard/parking-management/visitors' }} />
  }

  if (!visitorParkingAvailable && isVisitorsPath) {
    return <Navigate to={{ path: '/dashboard/building-dashboard/parking-management/residents' }} />
  }

  const handleDownloadTemplate = () => {
    downloadHref(PARKING_MANAGEMENT_TEMPLATE_PATH, 'Parking_Management_Template.csv')
  }

  const actions = isVisitorsPath ? (
    <>
      <Button use="secondary" size="small" onClick={handleParkingQRCodeClick}>
        <Icon icon={QrCodeIcon} />
        {t('parking-management:page.actions.parkingQrCode')}
      </Button>
      <Button use="secondary" size="small" onClick={handleSetServiceFeesClick}>
        <Icon icon={HandCoinsIcon} />
        {t('parking-management:page.actions.setServiceFees')}
      </Button>
    </>
  ) : isResidentsPath ? (
    <>
      <Button use="secondary" size="small" onClick={handleDownloadTemplate}>
        <Icon icon={DownloadSimpleIcon} />
        {t('parking-management:page.actions.downloadTemplate')}
      </Button>
      <Button
        use="primary"
        size="small"
        disabled={!buildingId}
        loading={isUploadParkingCsvPending}
        onClick={handleUploadCsvClick}
      >
        <Icon icon={UploadSimpleIcon} />
        {t('parking-management:page.actions.uploadCsv')}
      </Button>
      <S.HiddenInput {...inputProps} />
    </>
  ) : null

  return (
    <Page>
      <PageHeader
        title={t('parking-management:page.title')}
        actions={actions}
        navbarItems={[
          ...(parkingManagementAvailable ? [getResidentsNavbarItem(t)] : []),
          ...(visitorParkingAvailable ? [getVisitorsNavbarItem(t)] : []),
        ]}
      />
      <Outlet
        context={
          {
            isUploadParkingCsvPending,
            onUploadCSV: handleUploadCsvClick,
          } satisfies ParkingManagementOutletContext
        }
      />
    </Page>
  )
}
